/**
 * VizWidget.jsx - the build-your-own chart: pick a dataset and a chart type.
 * Deliberately separate from the fixed sections so adding a dataset is a
 * one-line change to VIZ_SOURCES.
 */
import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from 'recharts';
import { Icon } from '../../components/ui/Icon';
import { fmt } from '../../utils/format';
import { CARD, COLORS, TICK, TT } from './reportingTokens';

/* ===== DATA VISUALIZATION STUDIO ===== */
export const VIZ_SOURCES = [
  { id: 'pipeline_cust', label: 'Open Pipeline by Customer', rev: true },
  { id: 'won_rev_cust', label: 'Won Revenue by Customer', rev: true },
  { id: 'total_val_cust', label: 'Total Quoted Value by Customer', rev: true },
  { id: 'value_stage', label: 'Pipeline Value by Stage', rev: true },
  { id: 'bids_cust', label: 'Bids by Customer (Total)' },
  { id: 'bids_status', label: 'Bids by Status' },
  { id: 'wr_cust', label: 'Win Rate by Customer (3+ deals)' },
  { id: 'by_type', label: 'Projects by Type' },
  { id: 'by_division', label: 'Projects by Division' },
  { id: 'by_billing', label: 'Projects by Billing Type' },
  { id: 'by_engineer', label: 'Projects by Engineer (Top 10)' },
  { id: 'by_manager', label: 'Projects by Approval Manager (Top 10)' },
  { id: 'eng_status', label: 'Engineer Status Breakdown' },
  { id: 'eng_capacity', label: 'Engineer Capacity (Current Week)' },
];
export const VIZ_TYPES = [
  { id: 'bar', label: 'Bar' },
  { id: 'hbar', label: 'H-Bar' },
  { id: 'pie', label: 'Pie' },
];

export function VizWidget({ datasets, onExport }) {
  const [source, setSource] = useState('pipeline_cust');
  const [chartType, setChartType] = useState('hbar');
  const data = datasets[source] || [];
  const meta = VIZ_SOURCES.find((s) => s.id === source);
  const isRevenue = meta?.rev;

  const fmtV = (v) => (isRevenue ? fmt(v) : v);
  const SELECT = {
    padding: '7px 12px',
    borderRadius: 6,
    border: '1px solid #E0E0E0',
    fontSize: 11,
    fontFamily: 'Montserrat',
    outline: 'none',
    cursor: 'pointer',
    background: '#fff',
    color: '#333',
    fontWeight: 600,
  };

  const renderChart = () => {
    if (!data.length)
      return <div style={{ color: '#8A8A8A', fontSize: 11, textAlign: 'center', padding: 60 }}>No data available for this selection</div>;
    if (chartType === 'pie') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="45%"
              innerRadius={50}
              outerRadius={90}
              dataKey="value"
              paddingAngle={3}
              strokeWidth={2}
              stroke="#fff"
            >
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 9, fontFamily: 'Montserrat', fontWeight: 600 }}
              formatter={(v, e) => `${v} (${isRevenue ? fmt(e.payload.value) : e.payload.value})`}
            />
            <Tooltip contentStyle={TT} formatter={(v, n) => [fmtV(v), n]} />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    if (chartType === 'hbar') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" barSize={14}>
            <XAxis
              type="number"
              tick={TICK}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => (isRevenue ? `$${(v / 1e6).toFixed(1)}M` : v)}
            />
            <YAxis
              dataKey="name"
              type="category"
              tick={{ ...TICK, fontSize: 10, fill: '#333', fontWeight: 700 }}
              axisLine={false}
              tickLine={false}
              width={160}
            />
            <Tooltip contentStyle={TT} formatter={(v) => [fmtV(v), 'Value']} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]}>
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barSize={24}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F4" vertical={false} />
          <XAxis dataKey="name" tick={{ ...TICK, fill: '#555' }} axisLine={false} tickLine={false} interval={0} />
          <YAxis
            tick={TICK}
            axisLine={false}
            tickLine={false}
            width={45}
            tickFormatter={(v) => (isRevenue ? `$${(v / 1e6).toFixed(1)}M` : v)}
          />
          <Tooltip contentStyle={TT} formatter={(v) => [fmtV(v), 'Value']} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]}>
            {data.map((_, i) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <div>
            <div
              style={{ fontSize: 9, fontWeight: 700, color: '#5A5A5A', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 }}
            >
              Data Source
            </div>
            <select
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setChartType(
                  e.target.value.includes('cust') || e.target.value.includes('engineer') || e.target.value.includes('manager')
                    ? 'hbar'
                    : 'bar'
                );
              }}
              style={{ ...SELECT, minWidth: 220 }}
            >
              {VIZ_SOURCES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <span style={{ fontSize: 10, color: '#5A5A5A', fontWeight: 600, marginBottom: 8 }}>{data.length} items</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <div>
            <div
              style={{ fontSize: 9, fontWeight: 700, color: '#5A5A5A', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 }}
            >
              Chart Type
            </div>
            <div style={{ display: 'flex', gap: 3 }}>
              {VIZ_TYPES.map((ct) => (
                <button
                  key={ct.id}
                  onClick={() => setChartType(ct.id)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 6,
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'Montserrat',
                    border: `1px solid ${chartType === ct.id ? '#1B5E20' : '#E0E0E0'}`,
                    background: chartType === ct.id ? '#E8F5E9' : '#fff',
                    color: chartType === ct.id ? '#1B5E20' : '#666',
                  }}
                >
                  {ct.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => onExport && onExport(data, meta?.label || 'chart')}
            style={{
              ...SELECT,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginBottom: 0,
              color: '#666',
              border: '1px solid #E8E8E8',
            }}
          >
            <Icon name="download" size={11} color="#666" /> CSV
          </button>
        </div>
      </div>
      <div style={{ height: 280 }}>{renderChart()}</div>
    </div>
  );
}
